import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');

  const navigationItems = [
  { id: 'solution', label: 'Solution Overview', href: '#solution' },
  { id: 'roi-calculator', label: 'ROI Calculator', href: '#roi-calculator' },
  { id: 'success-stories', label: 'Success Stories', href: '#success-stories' },
  { id: 'pricing', label: 'Pricing', href: '#pricing' },
  { id: 'implementation', label: 'Implementation', href: '#implementation' }];


  const translations = {
    en: {
      solution: 'Solution Overview',
      roiCalculator: 'ROI Calculator',
      successStories: 'Success Stories',
      pricing: 'Pricing',
      implementation: 'Implementation',
      requestDemo: 'Request Demo',
      brandName: 'AlumERP',
      tagline: 'Aluminum Extrusion ERP',
      menu: 'Menu',
      close: 'Close'
    },
    hi: {
      solution: 'समाधान अवलोकन',
      roiCalculator: 'ROI कैलकुलेटर',
      successStories: 'सफलता की कहानियां',
      pricing: 'मूल्य निर्धारण',
      implementation: 'कार्यान्वयन',
      requestDemo: 'डेमो का अनुरोध',
      brandName: 'AlumERP',
      tagline: 'एल्यूमीनियम एक्सट्रूज़न ERP',
      menu: 'मेनू',
      close: 'बंद करें'
    }
  };

  const t = translations[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 20);

      const sections = navigationItems.map((item) => ({
        id: item.id,
        element: document.getElementById(item.id)
      }));

      const currentSection = sections.find((section) => {
        if (!section.element) return false;
        const rect = section.element.getBoundingClientRect();
        return rect.top <= 100 && rect.bottom >= 100;
      });

      if (currentSection) {
        setActiveSection(currentSection.id);
      }
    };

    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [navigationItems]);

  const handleNavClick = (href) => {
    const targetId = href.replace('#', '');
    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      const headerHeight = 80;
      const targetPosition = targetElement.offsetTop - headerHeight;

      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }

    setIsMenuOpen(false);
  };

  const handleDemoRequest = () => {
    const demoSection = document.getElementById('request-demo');
    if (demoSection) {
      const headerHeight = 80;
      const targetPosition = demoSection.offsetTop - headerHeight;

      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-100 transition-smooth shadow-card border-b border-border bg-background/95 backdrop-blur-sm`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo Section */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-gradient-primary rounded-lg">
              <Icon
                name="Factory"
                size={24}
                color="white"
                strokeWidth={2} />
            </div>
            <div className="flex flex-col justify-center">
              <h1 className="text-xl font-bold text-primary font-inter leading-tight">
                {t.brandName}
              </h1>
              <p className="text-xs text-text-secondary font-medium leading-tight">
                {t.tagline}
              </p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-2 ml-8">
            {navigationItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.href)}
                className={`relative px-4 py-2 rounded-lg font-medium transition-smooth text-base
                  ${activeSection === item.id ? 'text-primary bg-primary-50 shadow-card' : 'text-text-secondary hover:text-primary hover:bg-surface'}
                `}
                style={{ minWidth: '120px' }}
              >
                {currentLanguage === 'hi' ?
                  item.id === 'solution' ? t.solution :
                  item.id === 'roi-calculator' ? t.roiCalculator :
                  item.id === 'success-stories' ? t.successStories :
                  item.id === 'pricing' ? t.pricing :
                  item.id === 'implementation' ? t.implementation :
                  item.label : item.label}
                {activeSection === item.id && (
                  <div className="absolute left-4 right-4 bottom-1 h-1 rounded-full bg-gradient-accent transition-all duration-200" />
                )}
              </button>
            ))}
          </nav>

          {/* CTA Button - Desktop */}
          <div className="hidden md:block ml-6">
            <Button
              variant="primary"
              onClick={handleDemoRequest}
              iconName="Calendar"
              iconPosition="left"
              iconSize={18}
              className="shadow-cta hover-scale"
            >
              {t.requestDemo}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="md:hidden p-2 rounded-lg text-text-secondary hover:text-primary hover:bg-surface transition-smooth focus-ring"
            aria-label={isMenuOpen ? t.close : t.menu}
          >
            <Icon
              name={isMenuOpen ? "X" : "Menu"}
              size={24}
              strokeWidth={2}
            />
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-background border-t border-border shadow-lg animate-slide-down rounded-b-xl mx-2 mt-2">
          <div className="px-4 py-4 space-y-2">
            {navigationItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.href)}
                className={`relative block w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-smooth
                  ${activeSection === item.id ? 'text-primary bg-primary-50 shadow-card' : 'text-text-secondary hover:text-primary hover:bg-surface'}
                `}
                style={{ minWidth: '120px' }}
              >
                {currentLanguage === 'hi' ?
                  item.id === 'solution' ? t.solution :
                  item.id === 'roi-calculator' ? t.roiCalculator :
                  item.id === 'success-stories' ? t.successStories :
                  item.id === 'pricing' ? t.pricing :
                  item.id === 'implementation' ? t.implementation :
                  item.label : item.label}
                {activeSection === item.id && (
                  <div className="absolute left-4 right-4 bottom-1 h-1 rounded-full bg-gradient-accent transition-all duration-200" />
                )}
              </button>
            ))}
            {/* Mobile CTA Button */}
            <div className="pt-4 border-t border-border">
              <Button
                variant="primary"
                onClick={handleDemoRequest}
                className="w-full bg-gradient-accent hover:bg-accent-600 shadow-cta hover-scale"
                iconName="Calendar"
                iconPosition="left"
                iconSize={18}
              >
                {t.requestDemo}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Progress Indicator */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-surface-200">
        <div
          className="h-full bg-gradient-accent transition-all duration-300 ease-out"
          style={{
            width: `${Math.min(100, window.scrollY / (document.documentElement.scrollHeight - window.innerHeight) * 100)}%`
          }}
        />
      </div>
    </header>
  );
};

export default Header;